import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngrx/store';
import {
  selectImpersonatedUser,
  selectIsImpersonating,
} from '@core/auth/auth.selectors';
import { authApiActions } from '@core/auth/auth.actions';

/**
 * Banner component that displays a warning strip when an admin is impersonating another user.
 * Follows the same design language as EnvironmentBannerComponent for visual consistency.
 * Renders in app.component.html alongside the environment banner.
 */
@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule],
  template: `
    @if (isImpersonating()) {
      <div class="impersonation-banner" role="alert">
        <div class="impersonation-banner__content">
          <mat-icon class="impersonation-banner__icon"
            >supervisor_account</mat-icon
          >
          <span class="impersonation-banner__text">
            Impersonating
            <strong>{{ impersonatedDisplayName() }}</strong>
          </span>
        </div>
        <button
          mat-button
          class="impersonation-banner__stop-btn"
          (click)="stopImpersonation()"
          aria-label="Stop impersonating user"
        >
          <mat-icon>person_off</mat-icon>
          Stop
        </button>
      </div>
    }
  `,
  styleUrl: './impersonation-banner.component.scss',
})
export class ImpersonationBannerComponent {
  private readonly store = inject(Store);

  protected readonly isImpersonating = this.store.selectSignal(selectIsImpersonating);
  private readonly impersonatedUser = this.store.selectSignal(selectImpersonatedUser);

  protected readonly impersonatedDisplayName = computed(() => {
    const user = this.impersonatedUser();
    if (!user?.fullName) return '';
    const displayFirst =
      user.fullName.preferred || user.fullName.first || '';
    const surname = user.fullName.surname || '';
    return `${displayFirst} ${surname}`.trim();
  });

  /** Emitted when impersonation is stopped (for parent coordination) */
  readonly stopped = output<void>();

  stopImpersonation(): void {
    this.store.dispatch(authApiActions.stopUserImpersonation());
    this.stopped.emit();
  }
}
